"use client";

import { useState } from "react";
import { addAuthCartItem, clearAuthCart, loadAuthCartItems, removeAuthCartItem, updateAuthCartItemQty } from "./actions";
import type { CartItem } from "./CartContext";
import type { CartAddon } from "./addons";
import { addonsKey } from "./addons";
import { cartItemKey } from "./cartItemKey";
export type AuthCartHook = {
  items: CartItem[];
  /** True once the initial fetch has settled; false during SSR and first paint. */
  hydrated: boolean;
  loadItems: (userId: string) => Promise<void>;
  addItem: (userId: string, productId: string, qty: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  updateQty: (userId: string, productId: string, qty: number, minQty?: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  removeItem: (userId: string, productId: string, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  clearCart: (userId: string) => Promise<void>;
};
export function useAuthCart(): AuthCartHook {
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);
  async function loadItems(userId: string): Promise<void> {
    try {
      const next = await loadAuthCartItems(userId);
      setItems(next);
    } finally {
      // A failed load is still a settled answer. Without the `finally` a
      // network error would strand the customer on a skeleton permanently.
      setHydrated(true);
    }
  }
  async function addItem(userId_0: string, productId: string, qty: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null): Promise<void> {
    await addAuthCartItem({
      userId: userId_0,
      productId,
      qty,
      variantId,
      addons,
      productUnitId
    });
    // Reload to reflect server-side merge of qty for existing rows
    await loadItems(userId_0);
  }
  async function updateQty(userId_1: string, productId_0: string, qty_0: number, minQty = 1, variantId_0?: string | null, addons_0?: CartAddon[] | null, productUnitId_0?: string | null): Promise<void> {
    const floored = Math.max(qty_0, minQty);
    // minQty stays local: it floors the optimistic value shown until the next
    // load. The server applies the product's own minimum regardless.
    await updateAuthCartItemQty({
      userId: userId_1,
      productId: productId_0,
      qty: floored,
      variantId: variantId_0,
      addons: addons_0,
      productUnitId: productUnitId_0
    });
    const key = cartItemKey(productId_0, variantId_0, addonsKey(addons_0), productUnitId_0);
    setItems(prev => prev.map(i => cartItemKey(i.product_id, i.variant_id, addonsKey(i.addons), i.product_unit_id) === key ? {
      ...i,
      quantity: floored
    } : i));
  }
  async function removeItem(userId_2: string, productId_1: string, variantId_1?: string | null, addons_1?: CartAddon[] | null, productUnitId_1?: string | null): Promise<void> {
    await removeAuthCartItem({
      userId: userId_2,
      productId: productId_1,
      variantId: variantId_1,
      addons: addons_1,
      productUnitId: productUnitId_1
    });
    const key_0 = cartItemKey(productId_1, variantId_1, addonsKey(addons_1), productUnitId_1);
    setItems(prev_0 => prev_0.filter(i_0 => cartItemKey(i_0.product_id, i_0.variant_id, addonsKey(i_0.addons), i_0.product_unit_id) !== key_0));
  }
  async function clearCart(userId_3: string): Promise<void> {
    await clearAuthCart(userId_3);
    setItems([]);
  }
  return {
    items,
    hydrated,
    loadItems,
    addItem,
    updateQty,
    removeItem,
    clearCart
  };
}